import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Mail, Send, CheckCircle2, XCircle, Users, X } from "lucide-react";
import RichTextEditor from "../components/RichTextEditor";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-800",
  pending_approval: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  sent: "bg-blue-100 text-blue-900",
  rejected: "bg-red-100 text-red-900",
};
const STATUS_LABEL = {
  draft: "Bozza",
  pending_approval: "In attesa di approvazione",
  approved: "Approvata",
  sent: "Inviata",
  rejected: "Rifiutata",
};

export default function AdminMailing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [audience, setAudience] = useState(0);
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "personale" || user?.role === "admin";

  useEffect(() => {
    if (!loading && !isStaff) navigate("/login");
  }, [user, loading, navigate, isStaff]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/campaigns${filter !== "all" ? `?status=${filter}` : ""}`);
      setCampaigns(r.data);
      const a = await api.get("/campaigns/audience/count");
      setAudience(a.data.count);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [filter, user]);

  const approve = async (c) => {
    if (!window.confirm(`Approvare e inviare "${c.subject}" a ${audience} destinatari?`)) return;
    try {
      await api.post(`/campaigns/${c.campaign_id}/approve`);
      toast.success("Campagna approvata e in invio");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reject = async (c) => {
    if (!window.confirm(`Rifiutare la bozza "${c.subject}"?`)) return;
    try {
      await api.post(`/campaigns/${c.campaign_id}/reject`);
      toast.success("Bozza rifiutata");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Eliminare "${c.subject}"?`)) return;
    try {
      await api.delete(`/campaigns/${c.campaign_id}`);
      toast.success("Eliminata");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!isStaff) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Marketing</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2"><Mail className="w-7 h-7"/> Mailing list</h1>
            <p className="text-sm text-slate-600 mt-1"><Users className="w-4 h-4 inline mr-1"/>{audience} clienti iscritti alla mailing list</p>
          </div>
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-56" data-testid="campaign-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="pending_approval">In attesa</SelectItem>
                <SelectItem value="draft">Bozze</SelectItem>
                <SelectItem value="sent">Inviate</SelectItem>
                <SelectItem value="rejected">Rifiutate</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setCreateOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="campaign-new-btn">
              <Plus className="w-4 h-4 mr-2"/> Nuova campagna
            </Button>
          </div>
        </div>

        {busy && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>}
        {!busy && campaigns.length === 0 && (
          <Card className="p-12 text-center border-dashed">
            <p className="text-slate-600">Nessuna campagna. Iniziamone una?</p>
            <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-blue-800">Crea la prima campagna</Button>
          </Card>
        )}
        <div className="space-y-3">
          {campaigns.map(c => (
            <Card key={c.campaign_id} className="p-5" data-testid={`campaign-${c.campaign_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                    <span className="text-xs text-slate-500 font-mono">{c.campaign_id}</span>
                  </div>
                  <h3 className="heading font-bold text-lg text-slate-900 truncate">{c.subject}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Creata da <b>{c.created_by_name}</b> ({c.created_by_role}) · {new Date(c.created_at).toLocaleString("it-IT")}
                    {c.status === "sent" && c.sent_count !== undefined && ` · ${c.sent_count}/${c.recipients_total} inviate`}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => setEditCampaign(c)} data-testid={`campaign-view-${c.campaign_id}`}>
                    {c.status === "sent" || c.status === "rejected" ? "Visualizza" : "Modifica"}
                  </Button>
                  {isAdmin && (c.status === "pending_approval" || c.status === "draft") && (
                    <>
                      <Button size="sm" onClick={() => approve(c)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`campaign-approve-${c.campaign_id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1"/>{c.status === "draft" ? "Invia" : "Approva & invia"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(c)} className="text-red-600" data-testid={`campaign-reject-${c.campaign_id}`}>
                        <XCircle className="w-4 h-4 mr-1"/>Rifiuta
                      </Button>
                    </>
                  )}
                  {c.status !== "sent" && (isAdmin || c.created_by === user.user_id) && (
                    <Button size="sm" variant="outline" onClick={() => del(c)} className="text-red-600" data-testid={`campaign-del-${c.campaign_id}`}>Elimina</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <CampaignDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load}/>
      <CampaignDialog open={!!editCampaign} onOpenChange={(v)=>!v && setEditCampaign(null)} campaign={editCampaign} onSaved={()=>{setEditCampaign(null); load();}} isAdmin={isAdmin} me={user}/>
    </div>
  );
}

function CampaignDialog({ open, onOpenChange, campaign, onSaved, isAdmin, me }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [segCats, setSegCats] = useState([]);
  const [segCities, setSegCities] = useState([]);
  const [segOptions, setSegOptions] = useState({ categories: [], cities: [] });
  const [segAudience, setSegAudience] = useState(null);
  const readOnly = campaign && (campaign.status === "sent" || (campaign.status === "rejected" && !isAdmin) || (!isAdmin && campaign?.created_by !== me?.user_id));

  useEffect(() => {
    setSubject(campaign?.subject || "");
    setBody(campaign?.body || "");
    setSegCats(campaign?.segment_categories || []);
    setSegCities(campaign?.segment_cities || []);
  }, [campaign, open]);

  useEffect(() => {
    if (!open) return;
    api.get("/campaigns/segments/options").then(r => setSegOptions(r.data)).catch(()=>{});
  }, [open]);

  // Audience preview whenever segments change
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/campaigns/audience/preview", { segment_categories: segCats, segment_cities: segCities });
        if (!cancelled) setSegAudience(r.data.count);
      } catch { if (!cancelled) setSegAudience(null); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [segCats, segCities, open]);

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  const save = async () => {
    setBusy(true);
    try {
      if (campaign) {
        await api.patch(`/campaigns/${campaign.campaign_id}`, { subject, body, segment_categories: segCats, segment_cities: segCities });
        toast.success("Modifiche salvate");
      } else {
        const r = await api.post("/campaigns", { subject, body, segment_categories: segCats, segment_cities: segCities });
        const status = r.data.status;
        if (status === "pending_approval") toast.success("Bozza inviata agli amministratori per approvazione");
        else toast.success("Bozza salvata");
      }
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const sendNow = async () => {
    if (!campaign) return;
    if (!window.confirm(`Inviare subito questa campagna a ${segAudience ?? "?"} destinatari?`)) return;
    setBusy(true);
    try {
      // save changes first if any
      await api.patch(`/campaigns/${campaign.campaign_id}`, { subject, body, segment_categories: segCats, segment_cities: segCities });
      await api.post(`/campaigns/${campaign.campaign_id}/approve`);
      toast.success("Campagna inviata");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const hasSegments = segCats.length > 0 || segCities.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><Mail className="w-5 h-5 text-blue-800"/>{campaign ? "Campagna" : "Nuova campagna"}</DialogTitle>
          <DialogDescription>
            {campaign ? `Stato: ${STATUS_LABEL[campaign.status]}` : (isAdmin ? "Come admin puoi salvare come bozza e inviare direttamente" : "La bozza verrà inviata agli amministratori per l'approvazione")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Oggetto</label>
            <Input value={subject} onChange={e=>setSubject(e.target.value)} disabled={readOnly} data-testid="campaign-subject"/>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Corpo</label>
            {readOnly ? (
              <div className="border border-slate-300 rounded-md p-4 bg-slate-50 prose prose-sm max-w-none min-h-[220px]" dangerouslySetInnerHTML={{ __html: body }} data-testid="campaign-body-readonly"/>
            ) : (
              <RichTextEditor value={body} onChange={setBody} testId="campaign-body" minHeight={260}/>
            )}
          </div>
          <div className="border rounded-md p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">Segmentazione destinatari</div>
              <div className="text-xs text-blue-900 font-semibold" data-testid="segment-audience-count">
                <Users className="w-3.5 h-3.5 inline mr-1"/>{segAudience === null ? "…" : segAudience} destinatari
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
              {hasSegments ? "Verranno raggiunti solo i clienti con almeno un preventivo che rispetta i filtri (e opt-in attivo)."
                           : "Nessun filtro: tutti i clienti iscritti alla mailing list."}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] font-semibold text-slate-600 mb-1">Categorie</div>
                <div className="flex flex-wrap gap-1.5" data-testid="segment-categories">
                  {segOptions.categories.length === 0 && <span className="text-xs text-slate-400">Nessuna categoria</span>}
                  {segOptions.categories.map(c => {
                    const active = segCats.includes(c);
                    return (
                      <button key={c} type="button" disabled={readOnly} onClick={() => toggle(segCats, setSegCats, c)}
                              className={`text-xs px-2 py-1 rounded-full border ${active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
                              data-testid={`seg-cat-${c}`}>
                        {c}{active && <X className="w-3 h-3 inline ml-1"/>}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-slate-600 mb-1">Città</div>
                <div className="flex flex-wrap gap-1.5" data-testid="segment-cities">
                  {segOptions.cities.length === 0 && <span className="text-xs text-slate-400">Nessuna città</span>}
                  {segOptions.cities.map(c => {
                    const active = segCities.includes(c);
                    return (
                      <button key={c} type="button" disabled={readOnly} onClick={() => toggle(segCities, setSegCities, c)}
                              className={`text-xs px-2 py-1 rounded-full border ${active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
                              data-testid={`seg-city-${c}`}>
                        {c}{active && <X className="w-3 h-3 inline ml-1"/>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="border rounded-md p-3 bg-white">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Anteprima email</div>
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: body || "<p class='text-slate-400'>Vuota</p>" }} data-testid="campaign-preview"/>
          </div>
          {!readOnly && (
            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={busy || !subject.trim() || !body.trim()} className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="campaign-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : (campaign ? "Salva modifiche" : "Salva bozza")}
              </Button>
              {isAdmin && campaign && (campaign.status === "draft" || campaign.status === "pending_approval") && (
                <Button onClick={sendNow} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="campaign-send-now">
                  <Send className="w-4 h-4 mr-2"/>Invia ora
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
